import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './LandingPage.css';

const schedule = [
  { time: '1:00 PM – 2:00 PM', event: 'Welcome Drink & Refreshments', desc: 'Networking and light refreshments for all guests' },
  { time: '2:00 PM – 4:00 PM', event: 'Grand Opening ALS', desc: 'Official ceremony and celebration of the grand opening' }
];

/* ─── Lightbox ─── */
function Lightbox({ images, index, onClose, onPrev, onNext }) {
  const [slideClass, setSlideClass]     = useState('');
  const [dragOffset, setDragOffset]     = useState(0);
  const [isDragging, setIsDragging]     = useState(false);
  const touchStartX  = React.useRef(null);
  const touchStartY  = React.useRef(null);
  const slideTimer   = React.useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const handle = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft')  goPrev();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, index, images.length]);

  // Trigger slide animation in correct direction, then navigate
  const goNext = () => {
    clearTimeout(slideTimer.current);
    setSlideClass('lb-slide-next');
    onNext();
    slideTimer.current = setTimeout(() => setSlideClass(''), 380);
  };
  const goPrev = () => {
    clearTimeout(slideTimer.current);
    setSlideClass('lb-slide-prev');
    onPrev();
    slideTimer.current = setTimeout(() => setSlideClass(''), 380);
  };

  // Touch handlers — live drag + swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    setIsDragging(false);
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy)) {
      e.preventDefault();          // stop page scroll during horizontal drag
      setIsDragging(true);
      setDragOffset(dx * 0.65);    // image follows finger at 65% speed (rubber-band feel)
    }
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;

    setDragOffset(0);
    setIsDragging(false);

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else         goPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  if (!images.length) return null;
  const img = images[index];

  return (
    <div
      className="lb-overlay"
      onClick={onClose}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="lb-close" onClick={onClose}>✕</button>

      {images.length > 1 && (
        <button className="lb-arrow lb-prev" onClick={e => { e.stopPropagation(); goPrev(); }}>&#8249;</button>
      )}

      {/* Content: drag offset applied here; slide animation on img via key */}
      <div
        className="lb-content"
        onClick={e => e.stopPropagation()}
        style={{
          transform: `translateX(${dragOffset}px)`,
          transition: isDragging ? 'none' : 'transform 0.22s ease-out'
        }}
      >
        <img
          key={index}
          src={img.src}
          alt={img.caption || ''}
          className={`lb-image ${slideClass}`}
        />
        {img.caption && <div className="lb-caption">{img.caption}</div>}
      </div>

      {images.length > 1 && (
        <button className="lb-arrow lb-next" onClick={e => { e.stopPropagation(); goNext(); }}>&#8250;</button>
      )}

      {images.length > 1 && (
        <div className="lb-counter">{index + 1} / {images.length}</div>
      )}

      {/* Dot indicators — mobile only */}
      {images.length > 1 && (
        <div className="lb-swipe-hint">
          {images.map((_, i) => (
            <div key={i} className={`lb-dot${i === index ? ' active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Gallery grid (promo / event / dresscode) ─── */
function GalleryGrid({ images, tall, onOpen }) {
  if (!images || images.length === 0) return null;
  const flat = images.map(img => ({ src: `/uploads/gallery/${img.filename}`, caption: img.caption }));
  return (
    <div className={`lp-gallery-grid ${tall ? 'tall' : ''}`}>
      {images.map((img, i) => (
        <div key={img._id} className="lp-gcard" onClick={() => onOpen(flat, i)}>
          <img src={`/uploads/gallery/${img.filename}`} alt={img.caption || 'ALS Event'} loading="lazy" />
          <div className="lp-gcard-zoom">🔍</div>
          {img.caption && <div className="lp-gcaption">{img.caption}</div>}
        </div>
      ))}
    </div>
  );
}

/* ─── Masonry gallery (venue) ─── */
function GalleryMasonry({ images, onOpen }) {
  if (!images || images.length === 0) return null;
  const flat = images.map(img => ({ src: `/uploads/gallery/${img.filename}`, caption: img.caption }));
  return (
    <div className="lp-masonry">
      {images.map((img, i) => (
        <div key={img._id} className="lp-masonry-item" onClick={() => onOpen(flat, i)}>
          <img src={`/uploads/gallery/${img.filename}`} alt={img.caption || 'Venue'} loading="lazy" />
          <div className="lp-gcard-zoom">🔍</div>
          {img.caption && <div className="lp-gcaption">{img.caption}</div>}
        </div>
      ))}
    </div>
  );
}

/* ─── Main page ─── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [gallery, setGallery]   = useState({ promo: [], event: [], venue: [], dresscode: [] });
  const [logoSrc, setLogoSrc]   = useState('');
  const [lb, setLb]             = useState({ open: false, images: [], index: 0 });

  useEffect(() => {
    api.get('/admin/payment-info').then(r => {
      if (r.data.logoImage) setLogoSrc(`/uploads/logo/${r.data.logoImage}`);
    }).catch(() => {});
    api.get('/gallery').then(r => {
      const g = { promo: [], event: [], venue: [], dresscode: [] };
      r.data.forEach(img => { if (g[img.category]) g[img.category].push(img); });
      setGallery(g);
    }).catch(() => {});
  }, []);

  const openLightbox = useCallback((images, index) => setLb({ open: true, images, index }), []);
  const closeLightbox = useCallback(() => setLb(s => ({ ...s, open: false })), []);
  const prevImage = useCallback(() => setLb(s => ({ ...s, index: (s.index - 1 + s.images.length) % s.images.length })), []);
  const nextImage = useCallback(() => setLb(s => ({ ...s, index: (s.index + 1) % s.images.length })), []);

  return (
    <div className="lp">

      {/* Lightbox */}
      {lb.open && (
        <Lightbox images={lb.images} index={lb.index} onClose={closeLightbox} onPrev={prevImage} onNext={nextImage} />
      )}

      {/* ─── Nav ─── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <button className="lp-nav-staff" onClick={() => navigate('/admin/login')}>Staff Login</button>
          <div className="lp-nav-center" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            {logoSrc
              ? <img src={logoSrc} alt="ALS" className="lp-nav-logo-img" />
              : <span className="lp-nav-logo-text">ALS</span>}
          </div>
          <button className="lp-nav-book" onClick={() => navigate('/book')}>Book Ticket</button>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="lp-hero">
        <div className="lp-hero-bg" />
        <div className="lp-hero-lines" />
        <div className="lp-hero-content">
          <div className="lp-badge"><span className="lp-badge-dot" />June 6, 2026 &nbsp;·&nbsp; Phnom Penh</div>
          <div className="lp-hero-tag">You Are Invited To</div>
          <h1 className="lp-hero-title">GRAND<br/>OPENING</h1>
          <p className="lp-hero-subtitle">ALS &nbsp;·&nbsp; Amazing Life Solutions</p>
          <div className="lp-info-row">
            <div className="lp-info-card"><span className="lp-info-icon">📅</span><div><div className="lp-info-label">Date</div><div className="lp-info-value">Saturday, June 6, 2026</div></div></div>
            <div className="lp-info-card"><span className="lp-info-icon">🕐</span><div><div className="lp-info-label">Time</div><div className="lp-info-value">1:00 PM – 4:00 PM</div></div></div>
            <div className="lp-info-card"><span className="lp-info-icon">📍</span><div><div className="lp-info-label">Location</div><div className="lp-info-value">រាជបណ្ឌិតសភាកម្ពុជា<small>សាលខេមរៈវិទូ</small></div></div></div>
          </div>
          <button className="lp-hero-cta" onClick={() => navigate('/book')}>Reserve My Seat →</button>
          <p className="lp-hero-note">Free entry · Limited seats available</p>
        </div>
      </section>

      {/* ─── Promo ─── */}
      {gallery.promo.length > 0 && (
        <section className="lp-gallery-section">
          <div className="lp-gallery-header">
            <p className="lp-section-tag">About ALS</p>
            <h2 className="lp-section-title2">Amazing Life <span>Solutions</span></h2>
          </div>
          <GalleryGrid images={gallery.promo} tall onOpen={openLightbox} />
        </section>
      )}

      {/* ─── Event ─── */}
      {gallery.event.length > 0 && (
        <section className="lp-gallery-section dark">
          <div className="lp-gallery-header">
            <p className="lp-section-tag">Event Experience</p>
            <h2 className="lp-section-title2">An Unforgettable <span>Night</span></h2>
          </div>
          <GalleryGrid images={gallery.event} onOpen={openLightbox} />
        </section>
      )}

      {/* ─── Venue ─── */}
      {gallery.venue.length > 0 && (
        <section className="lp-gallery-section">
          <div className="lp-gallery-header">
            <p className="lp-section-tag">The Venue</p>
            <h2 className="lp-section-title2">រាជបណ្ឌិតសភា<span>កម្ពុជា</span></h2>
            <p className="lp-gallery-sub">សាលខេមរៈវិទូ · Khemaravitu Theater</p>
          </div>
          <GalleryMasonry images={gallery.venue} onOpen={openLightbox} />
        </section>
      )}

      {/* ─── Dress Code ─── */}
      {gallery.dresscode.length > 0 && (
        <section className="lp-gallery-section dark">
          <div className="lp-gallery-header">
            <p className="lp-section-tag">Dress Code</p>
            <h2 className="lp-section-title2">Formal <span>Attire</span></h2>
            <p className="lp-gallery-sub">Smart formal or traditional Khmer dress for this special occasion</p>
          </div>
          <GalleryGrid images={gallery.dresscode} onOpen={openLightbox} />
        </section>
      )}

      {/* ─── Schedule ─── */}
      <div className="lp-section">
        <p className="lp-section-tag">Programme</p>
        <h2 className="lp-section-title">Event <span>Schedule</span></h2>
        <div className="lp-timeline">
          {schedule.map((item, i) => (
            <div key={i} className="lp-timeline-item">
              <div className="lp-timeline-dot" />
              <div className="lp-timeline-time">{item.time}</div>
              <div className="lp-timeline-event">{item.event}</div>
              <div className="lp-timeline-desc">{item.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── CTA ─── */}
      <section className="lp-cta-section">
        <h2 className="lp-cta-title">Don't Miss This Moment</h2>
        <p className="lp-cta-sub">Secure your spot now. Limited seats available for this exclusive event.</p>
        <button className="lp-cta-btn" onClick={() => navigate('/book')}>Book Your Ticket Now</button>
      </section>

      <footer className="lp-footer">© 2026 <span>Grand Opening ALS</span>. All rights reserved.</footer>
    </div>
  );
}
